import type { XmaxDisconnectReasonCode } from '../telemetry/startup'

/** Optional, privacy-safe evidence. A disconnect trigger is not a confirmed root cause. */
export type XmaxErrorDetails = Readonly<{
  reason_code?: XmaxDisconnectReasonCode
  failure_stage?: string
  trace_id?: string
  session_id?: string
  generation_id?: string
  failure_type?: 'business_error' | 'http_error' | 'timeout' | 'network_error' | 'invalid_response' | 'cancelled'
  http_status?: number
  business_code?: string
  rtc_error_code?: string
  connection_state?: string
  connection_reason?: string
  connection_recovered?: boolean
  connection_interrupted_at_ms?: number
  connection_recovered_at_ms?: number
  network_online?: boolean
  detection_source?: string
  stall_ms?: number
  reconnect_timeout_ms?: number
  heartbeat_failure_trigger?: string
  session_status?: string
  server_close_reason?: string
  teardown_failure_stages?: string
}>

const stringKeys = ['reason_code', 'failure_stage', 'trace_id', 'session_id', 'generation_id',
  'failure_type', 'business_code', 'rtc_error_code', 'connection_state', 'connection_reason',
  'detection_source', 'heartbeat_failure_trigger', 'session_status', 'server_close_reason',
  'teardown_failure_stages'] as const
const numberKeys = ['http_status', 'connection_interrupted_at_ms', 'connection_recovered_at_ms',
  'stall_ms', 'reconnect_timeout_ms'] as const
const booleanKeys = ['connection_recovered', 'network_online'] as const

/** @internal Allowlist only: never expose tokens, URLs, request bodies or native errors. */
export function createErrorDetails(values: Record<string, unknown>): XmaxErrorDetails {
  const result: Record<string, string | number | boolean> = {}
  for (const key of stringKeys) {
    const value = values[key]
    if (typeof value === 'string' && value.length) result[key] = value.slice(0, 256)
  }
  for (const key of numberKeys) {
    const value = values[key]
    if (typeof value === 'number' && Number.isFinite(value)) result[key] = value
  }
  for (const key of booleanKeys) {
    if (typeof values[key] === 'boolean') result[key] = values[key]
  }
  return Object.freeze(result) as XmaxErrorDetails
}
